<template>
  <transition name="fade" mode="out-in">
    <div v-show="displayed" class="help-overlay" @click="nextStep">
      <CloseButton white @click.stop="focusStepsGroup(stepsGroups.length)" />
      <div class="transparent-overlay" :style="transparentOverlayStyle" />

      <!-- Hint about help usage -->
      <div v-visible="displayed && displayTopContent" class="top-content">
        <div>
          {{ `${$t('help.HELP_USAGE')} (${stepProgress})` }}
        </div>
        <div v-if="stepsGroups.length > 1">
          {{ $t('help.HELP_GROUPS_USAGE') }}
        </div>
      </div>

      <!-- Help image and steps groups if any -->
      <div class="bottom-content">
        <img
          ref="neoHelp"
          src="/images/ia/help-full.png"
          class="ia"
          alt=""
        >
        <div v-if="stepsGroups.length > 1" class="group-titles">
          <div v-for="(group, index) in stepsGroups">
            <Icon v-if="index > 0">{{ arrowDownLine }}</Icon>
            <span
              class="content"
              :class="{ selected: index === currentStepsGroupIndex }"
              @click.stop="onStepsGroupClick($event, index)"
            >
              <span class="number">{{ index + 1 }}</span>
              <span class="title">{{ group.title }}</span>
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="!(currentElementsBounds === null && currentText.length === 0)"
        class="textbox-overlay"
        :class="{ boxed: boxed }" :style="textOverlayStyle"
      >
        <img :src="iaHelp" class="ia-head" alt="">
        <div
          :key="currentIndex"
          class="text"
          :style="textAboveLinksStyle"
          v-html="currentText"
        />
        <div
          v-for="(link, index) in currentStep.links"
          :key="index"
          class="ia-help-link"
          @click.stop="link.action"
        >
          {{ link.text }}
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Help assistant overlay
 */
import $ from 'jquery';
// Components
import CloseButton from '@/components/close-button.vue';
import Icon from '@/components/icon.vue';
// Utils & assets
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { arrowDownLine } from '@/assets/icons';

const iaHelp = require('@/assets/img/ia-help.png');


export default {

  name: 'HelpOverlay',

  components: {
    CloseButton,
    Icon,
  },

  props: {
    displayed: Boolean,
    boxed: {
      type: Boolean,
      default: true,
    },
    stepsGroups: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      iaHelp,
      arrowDownLine,
      // Steps are grouped in IAHelpStepsGroup objects, this is the index of the current group
      currentStepsGroupIndex: 0,
      // Index of the current step in the current group
      currentIndex: -1,
      marginLeft: 220,
      marginTop: 0,
    };
  },

  computed: {
    textMinHeight() {
      return this.boxed ? this.initialTextMinHeight + this.fontSize * 2.5 : this.initialTextMinHeight;
    },
    textMinWidth() {
      return this.boxed ? 300 : 280;
    },
    /** Number: Padding to add around the element that is focused using the transparent overlay */
    transOverlayPadding() {
      return 15;
    },
    /** Number: padding between the text and the trans overlay */
    padding() {
      return 20;
    },
    dblPadding() {
      return this.padding * 2;
    },
    /** Object: Bounding rect from elements */
    currentElementsBounds() {
      let width;
      let height;
      let top;
      let left;
      let bottom;
      let right;
      for (let i = 0, len = this.currentElts.length; i < len; i++) {
        const el = this.currentElts[i];
        if (!el || !(el instanceof Element || el instanceof HTMLDocument)) {
          return null;
        }
        const rect = this.currentElts[i].getBoundingClientRect();
        top = top !== undefined ? Math.min(top, rect.top) : rect.top;
        left = left !== undefined ? Math.min(left, rect.left) : rect.left;
        bottom = bottom !== undefined ? Math.max(bottom, rect.bottom) : rect.bottom;
        right = right !== undefined ? Math.max(right, rect.right) : rect.right;
      }
      if (top !== undefined) {
        height = bottom - top;
        width = right - left;
      }
      return {
        width,
        height,
        top,
        left,
        bottom,
        right,
      };
    },
    /** Object: bounds from currentElementsBounds with margins and padding */
    transparentOverlayBounds() {
      if (this.currentElementsBounds === null) {
        return null;
      }
      const rect = this.currentElementsBounds;
      return {
        width: rect.width + this.transOverlayPadding * 2,
        height: rect.height + this.transOverlayPadding * 2,
        left: rect.left - this.marginLeft - this.transOverlayPadding,
        top: rect.top - this.marginTop - this.transOverlayPadding,
      };
    },
    /** Object: CSS style applied to the highlighting overlay */
    transparentOverlayStyle() {
      if (this.transparentOverlayBounds === null) {
        return { top: '0px', left: '0px', width: '0px', height: '0px' };
      }
      const { top, left, width, height } = this.transparentOverlayBounds;
      return { top: `${top}px`, left: `${left}px`, width: `${width}px`, height: `${height}px` };
    },
    /** Object: bounds of the text when no elements focused */
    defaultTextOverlayBounds() {
      let left = this.$refs.neoHelp ? this.$refs.neoHelp.offsetWidth : 0;
      if (left === 0) {
        left = (window.innerHeight - this.marginTop) * 0.3 * 0.55; // IA Help image ratio
      }
      return {
        top: window.innerHeight - this.marginTop * 2 - this.textMinHeight,
        left: left + this.padding,
        width: this.textMinWidth,
        maxHeight: this.textMinHeight,
      };
    },
    /** Object: bounds of the text displayed next to the highlighting overlay */
    textOverlayBounds() {
      // eslint-disable-next-line
      const { dblPadding, padding, marginTop, marginLeft, textMinWidth, textMinHeight } = this;
      let top = marginTop + padding;
      let left = padding;
      if (this.currentElementsBounds !== null) {
        const rect = this.currentElementsBounds;
        // If elt isn't displayed, return default style
        if (rect.width === 0 && rect.height === 0) {
          return this.defaultTextOverlayBounds;
        }
        if (rect.top - dblPadding > textMinHeight + marginTop) {
          // Above highlight
          top = rect.top - textMinHeight - padding;
          left = this.adjustLeft(rect);
        } else if (rect.left - dblPadding - marginLeft > textMinWidth) {
          // To the left
          top = Math.max(marginTop, rect.top) + padding;
          left = rect.left - textMinWidth - padding - marginLeft;
        } else if (rect.bottom + padding + textMinHeight < window.innerHeight) {
          // Below
          top = rect.bottom + dblPadding;
          left = this.adjustLeft(rect);
        } else if (rect.right + textMinWidth + dblPadding < window.innerWidth) {
          // To the right
          top = Math.max(marginTop, rect.top) + padding;
          left = rect.right + dblPadding - marginLeft;
        }
      } else {
        return this.defaultTextOverlayBounds;
      }
      return { top: top - marginTop, left: left, width: textMinWidth, maxHeight: textMinHeight };
    },
    /** Text position */
    textOverlayStyle() {
      const { top, left, width, height } = this.textOverlayBounds;
      return { top: `${top}px`, left: `${left}px`, width: `${width}px`, height: `${height}px` };
    },
    currentText() {
      return this.currentStep.text;
    },
    /** Array: Array of elements to focus using transparent overlay */
    currentElts() {
      let { elt } = this.currentStep;
      if (!(elt instanceof Array)) {
        elt = [elt];
      }
      return elt.map((el) => {
        if (typeof el === 'string' && $(el)) {
          el = $(el)[0] || null;
        }
        if (el && el._isVue) {
          el = el.$el;
        }
        return el;
      });
    },
    /** Number: Default height of text div */
    initialTextMinHeight() {
      // nb of lines   *    font size   *   line height
      return 8 * this.fontSize * 1.5;
    },
    /** Object: Style applied to text div above links */
    textAboveLinksStyle() {
      return {
        maxHeight: `${this.textMinHeight - this.fontSize * 1.5}px`,
      };
    },
    /** Object: Currently selected group of steps */
    currentStepsGroup() {
      return this.stepsGroups[this.currentStepsGroupIndex];
    },
    /** Object: Currently displayed step */
    currentStep() {
      if (this.currentStepsGroup && this.currentIndex > -1 && this.currentStepsGroup.steps.length > this.currentIndex) {
        return this.currentStepsGroup.steps[this.currentIndex];
      }
      return new IAHelpStep();
    },
    fontSize() {
      return this.$store.getters.fontSize;
    },
    /** Boolean: Whether the top page hint should be displayed or not */
    displayTopContent() {
      // If the top offset of the focused element or the text overlay is above minHeight
      // Prevent displayin the top content
      const minHeight = this.fontSize * 3.5;
      if (!this.transparentOverlayBounds || !this.textOverlayBounds) {
        return true;
      }
      if (this.transparentOverlayBounds.top > minHeight && this.textOverlayBounds.top > minHeight) {
        return true;
      }
      return false;
    },
    /** String: Current progress of the help steps */
    stepProgress() {
      const totalSteps = this.stepsGroups.reduce((total, group) => total + group.steps.length, 0);
      let currentIndex = 0;
      if (this.stepsGroups.length > this.currentStepsGroupIndex) {
        for (let i = 0; i < this.currentStepsGroupIndex; i++) {
          currentIndex += this.stepsGroups[i].steps.length;
        }
        currentIndex += this.currentIndex;
      } else {
        currentIndex = totalSteps - 1;
      }
      return `${currentIndex + 1} / ${totalSteps}`;
    },
  },

  watch: {
    displayed(isDisplayed) {
      this.calculateSizes();
      const { currentStepsGroup } = this;
      if (isDisplayed && currentStepsGroup && currentStepsGroup.steps && currentStepsGroup.steps.length) {
        $(window).on(`resizeEnd.${this.uuid}.display`, this.onResizeHandler);
        this.currentStepsGroupIndex = 0;
        this.currentIndex = -1;
        this.nextStep();
      }
    },
  },

  mounted() {
    this.$nextTick(this.calculateSizes);
    $(window).on(this.resizeEventName, this.calculateSizes);
  },

  beforeDestroy() {
    $(window).off(`resizeEnd.${this.uuid}.display`, this.onResizeHandler);
  },

  methods: {
    adjustLeft(rect) {
      if (rect.left + this.textMinWidth + this.dblPadding > window.innerWidth) {
        return window.innerWidth - this.textMinWidth - this.padding - this.marginLeft;
      }
      return rect.left + this.dblPadding - this.marginLeft;
    },
    /** Top header and left nav sizes must be dynamically fetched */
    calculateSizes() {
      const nav = document.querySelector('.nav-bar');
      this.marginLeft = nav ? nav.offsetWidth : 220;
    },
    /** Unfocus the current step calling all necesary callbacks */
    endCurrentStep(next, evt) {
      if (this.currentStep && typeof this.currentStep.onNext === 'function') {
        // 'onNext' can use the second argument as a callback to call when any processing is done
        // If so, it must return true so goNext isn't called right after calling onNext
        const waitNextCallback = this.currentStep.onNext(evt, () => {
          this.$nextTick(next);
        });
        if (!waitNextCallback) {
          this.$nextTick(next);
        }
      } else {
        next();
      }
    },
    /** Unfocus the current steps group calling all necesary callbacks */
    endCurrentStepsGroup(next, evt) {
      if (this.currentStepsGroup && typeof this.currentStepsGroup.onNext === 'function') {
        // 'onNext' can use the second argument as a callback to call when any processing is done
        // If so, it must return true so goNext isn't called right after calling onNext
        const waitNextCallback = this.currentStepsGroup.onNext(evt, () => {
          this.$nextTick(next);
        });
        if (!waitNextCallback) {
          this.$nextTick(next);
        }
      } else {
        next();
      }
    },
    /** Go to the next step of the tutorial */
    nextStep(evt) {
      this.endCurrentStep(this.goNext, evt);
    },
    goNext() {
      if (this.currentIndex >= this.currentStepsGroup.steps.length - 1) {
        this.focusStepsGroup(this.currentStepsGroupIndex + 1);
      } else {
        this.currentIndex++;
      }
    },
    /** User wants to jump to another group of steps */
    onStepsGroupClick(evt, index) {
      this.endCurrentStep(() => {
        this.focusStepsGroup(index);
      }, evt);
    },
    /** Set the current group of steps to specified index */
    focusStepsGroup(index) {
      this.endCurrentStepsGroup(() => {
        if (index < this.stepsGroups.length) {
          this.currentStepsGroupIndex = index;
          this.currentIndex = 0;
        } else {
          this.hide();
        }
      });
    },
    /** Hide and reset the tutorial */
    hide() {
      $(window).off(`resizeEnd.${this.uuid}.display`, this.onResizeHandler);
      this.$emit('hide');
    },
    /** Refresh the highlighted elt on window resize */
    onResizeHandler() {
      this.currentIndex--;
      this.currentIndex++;
    },
  },

};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.help-overlay {
  position: fixed;
  top: 0px;
  left: $nav-width;
  height: 100%;
  width: calc(100% - #{$nav-width});
  z-index: 10016;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-flow: column;
  justify-content: space-between;

  .transparent-overlay {
    position: absolute;
    box-shadow: 0px 0px 0px 9999px rgba(0, 0, 0, 0.65), inset 0px 0px 16px rgba(0, 0, 0, 0.6);
    border-radius: 10px;
    transition: all 0.3s ease-in-out;
  }
  .textbox-overlay {
    color: rgb(255, 255, 255);
    font-weight: 600;
    position: absolute;

    div.text {
      max-height: calc(100% - 1em);
      line-height: 1.5em;
      overflow: hidden;
    }
    div.ia-help-link {
      color: #2EA2FF;
    }
    div.ia-help-link:hover {
      text-decoration: underline;
    }
    .ia-head {
      display: none;
      height: 3em;
      width: 3em;
      background: #ffffff;
      border-radius: 2000px;
      box-sizing: border-box;
      position: absolute;
      left: 50%;
      top: 0px;
      transform: translate(-50%, -50%);
    }
    &.boxed {
      background: rgb(255, 255, 255);
      border-radius: 20px;
      box-sizing: border-box;
      padding: 1.5em 1em 1em 1em;
      color: $color-main-text;

      .ia-head {
        display: block;
      }
    }
  }
  .next-btn {
    position: absolute;
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
  }

  button {
    background: #00A7E1;
    padding: 5px 15px;
    border-radius: 100px;
    border: none;
    color: #ffffff;
    margin-top: 15px;
    box-shadow: none;
  }

  .top-content {
    padding: 0.2em 5em;
    text-align: center;
    color: white;
    font-weight: 600;
    position: relative;
    font-style: italic;
  }

  .bottom-content {
    position: relative;
    width: 100%;
    height: 30%;
    display: flex;

    img.ia {
      height: 100%;
      width: auto;
    }
    .group-titles {
      display: flex;
      align-items: flex-end;
      color: white;
      padding-bottom: 1em;

      > div {
        display: flex;
        align-items: center;

        svg {
          transform: rotate(270deg);
          width: 0.85em;
          height: 0.85em;
          margin: 0 0.7em;
        }

        .content {
          display: flex;
          align-items: center;
          padding: 0.35em;
          border-radius: 0.5em;
          border: 0.15em solid white;

          &.selected, &:hover {
            background: white;
            color: $color-main-text;
          }
          .number {
            color: white;
            background: $color-light-blue-text;
            width: 1.5em;
            height: 1.5em;
            border-radius: 1em;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-right: 0.5em;
          }
        }
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .help-overlay {
    left: 40px;
    width: calc(100% - 40px);
    padding: 0px;

    button {
      margin-top: 7px;
      padding: 3px 12px;
    }
  }
}
</style>
